import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/datetime";
import { Card, PermBadge, EligibleBadge, STATUS, errMsg } from "@/components/email/EmParts";
import { ConsentForm, TagEditor } from "@/components/email/SubscriberEditors";

const Item = ({ k, v, testId }) => (<div><dt className="text-[11px] uppercase tracking-wider text-slate-400">{k}</dt><dd className="text-sm text-slate-800 mt-0.5 break-words" data-testid={testId}>{v || "—"}</dd></div>);

function Profile({ s, customer }) {
  return (
    <Card title="Subscriber" testId="em-detail-profile">
      <dl className="grid grid-cols-2 gap-4">
        <Item k="Email" v={s.email} testId="em-detail-email" /><Item k="Telephone" v={s.telephone} />
        <Item k="Customer type" v={s.customer_type} /><Item k="Location" v={[s.location?.city, s.location?.postcode].filter(Boolean).join(", ")} />
        <Item k="Source" v={s.source} /><Item k="Created" v={formatDateTime(s.created_at)} />
        <Item k="Linked customer" testId="em-detail-customer" v={customer ? <Link to={`/users/${customer.legacy_id}`} className="text-indigo-600 hover:underline">#{customer.legacy_id} {customer.full_name}</Link> : "Standalone subscriber"} />
        <Item k="Status" v={STATUS[s.subscription_status]} testId="em-detail-status" />
        <Item k="Last email sent" v={s.last_email_sent_at && formatDateTime(s.last_email_sent_at)} /><Item k="Last click" v={s.last_click_at && formatDateTime(s.last_click_at)} />
        <Item k="Campaigns received" v={String(s.campaigns_received)} /><Item k="Recorded clicks" v={String(s.total_clicks)} />
      </dl>
    </Card>
  );
}

function History({ events }) {
  return (
    <Card title="Consent history" testId="em-detail-history">
      {!events.length ? <p className="text-sm text-slate-500">No consent recorded. This subscriber cannot receive marketing.</p> : (
        <ul className="space-y-3">{events.map((e) => (
          <li key={e.id} className="text-sm border-l-2 pl-3 border-slate-200" data-testid={`em-consent-event-${e.id}`}>
            <p className="font-medium text-slate-800">{e.kind === "grant" ? "Consent granted" : "Consent withdrawn"} · {e.source}</p>
            <p className="text-xs text-slate-500">{formatDateTime(e.recorded_at)} · {e.actor}{e.evidence ? ` · ${e.evidence}` : ""}{e.reason ? ` · ${e.reason}` : ""}</p>
          </li>))}</ul>)}
    </Card>
  );
}

export default function EmSubscriberDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const manage = can("email_marketing.manage");
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/email-marketing/subscribers/${id}`).then((r) => setD(r.data)).catch((e) => setD({ error: errMsg(e) })), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  if (d.error) return <p className="text-sm text-rose-700" data-testid="em-detail-error">{d.error}</p>;
  const s = d.subscriber;
  return (
    <div className="space-y-4" data-testid="em-subscriber-detail">
      <Link to="/email-marketing/subscribers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="em-detail-back"><ArrowLeft className="w-4 h-4" />Subscribers</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-bold text-slate-900" data-testid="em-detail-name">{`${s.first_name} ${s.last_name}`.trim() || s.email || "Subscriber"}</h2>
        <PermBadge value={s.marketing_permission} testId="em-detail-permission" /><EligibleBadge sub={s} testId="em-detail-eligible" />
      </div>
      {d.suppression && <p className="text-sm rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2" data-testid="em-detail-suppressed">Suppressed ({d.suppression.reason}) since {formatDateTime(d.suppression.created_at)}: {d.suppression.source}</p>}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4"><Profile s={s} customer={d.customer} /><History events={d.consent_events} /></div>
        <div className="space-y-4">
          {manage && <ConsentForm sub={s} onDone={load} />}
          <TagEditor sub={s} manage={manage} onDone={load} />
        </div>
      </div>
    </div>
  );
}
